// 파싱된 노션 노드 트리를 우리 workspace 스키마로 적재한다.
//  0) zip 첨부(이미지/html/pdf 등) 중 본문에서 참조된 것만 workspace-uploads 업로드 (2026-07-06)
//  1) workspace.pages insert (부모 먼저 → parent_id 매핑)
//  2) 본문 상대경로 링크 → public URL/마커 재작성 → Yjs ystate → workspace.page_content upsert
//
// 진행 상황은 onProgress 콜백으로 보고한다.

import { supabase } from '../../supabaseClient';
import { orderForImport, collectAssetRefs, rewriteZipAttachments } from './notionZip';
import { markdownToYstate } from './markdownToYstate';

const BUCKET = 'workspace-uploads';

// 확장자 → Content-Type (임베드/이미지 렌더에 필요한 것 위주, 그 외 octet-stream)
const CT_BY_EXT = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp', ico: 'image/x-icon',
  html: 'text/html', htm: 'text/html', pdf: 'application/pdf', txt: 'text/plain',
  mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
  mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4',
};
function contentTypeOf(name) {
  const ext = ((String(name).match(/\.([A-Za-z0-9]+)$/) || [])[1] || '').toLowerCase();
  return CT_BY_EXT[ext] || 'application/octet-stream';
}

function randomId8() {
  try { return crypto.randomUUID().slice(0, 8); } catch (_e) {
    return Math.random().toString(36).slice(2, 10).padEnd(8, '0');
  }
}

/**
 * 본문에서 참조된 zip 첨부를 workspace-uploads 에 순차 업로드.
 * 경로 규칙은 edge notion-import 의 reupload 와 동일 계열: `notion-zip/<임의8>/<safe이름>`
 * (파일명 `[^\w.\-]+` → `_`, 80자 제한). 실패한 첨부는 errors 에 수집하고 링크는 원문 유지.
 * @returns {Promise<Map<string, string>>} zip 경로 → public URL
 */
async function uploadZipAssets(nodes, assets, errors, onProgress) {
  const urlByPath = new Map();
  if (!assets || !assets.size) return urlByPath;

  // 참조된 첨부만 선별 (미참조 파일 업로드 방지)
  const referenced = new Set();
  for (const n of nodes) {
    for (const p of collectAssetRefs(n.md, n, assets)) referenced.add(p);
  }
  const list = Array.from(referenced);
  for (let i = 0; i < list.length; i += 1) {
    const path = list[i];
    const asset = assets.get(path);
    if (!asset) continue;
    if (onProgress) {
      onProgress({ done: 0, total: nodes.length, title: `📎 첨부 업로드 ${i + 1}/${list.length} — ${asset.name}`, ok: true });
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const bytes = await asset.entry.async('uint8array');
      // ⚠️ 파일명에 '_' 를 남기지 않는다 ('-' 로 치환). 재작성된 :::embed/:::file 마커 라인은
      // markdownToYstate 의 마크다운 파서를 "일반 텍스트" 로 통과하는데, URL 안의 '_' 는
      // emphasis 구분자로 소비돼 embed url prop 이 실제 스토리지 키와 달라진다
      // (예: ______x.html → __x.html → iframe 404. 2026-07-06 "[7 3] OKR 2주차" 재가져오기 사고).
      // [A-Za-z0-9.\-] 만 남기면 마크다운-불활성 → 업로드 키 == 마커 URL 경로가 항상 보존된다.
      const safe = (asset.name || 'file').replace(/[^A-Za-z0-9.\-]+/g, '-').slice(0, 80) || 'file';
      const key = `notion-zip/${randomId8()}/${safe}`;
      // eslint-disable-next-line no-await-in-loop
      const { error: upErr } = await supabase.storage.from(BUCKET)
        .upload(key, bytes, { contentType: contentTypeOf(safe), upsert: false });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(key);
      if (!pub || !pub.publicUrl) throw new Error('public URL 조회 실패');
      urlByPath.set(path, pub.publicUrl);
    } catch (e) {
      // 실패한 첨부의 본문 링크는 기존 상대경로 그대로 유지된다(rewriteZipAttachments 가 스킵).
      errors.push({ title: asset.name, stage: 'attachment', message: e?.message || String(e) });
    }
  }
  return urlByPath;
}

/**
 * @param {Array} nodes - parseNotionZip 결과 노드 배열
 * @param {{ orgId: string, userId: string, assets?: Map }} ctx
 *   - assets: parseNotionZip 의 첨부 엔트리 Map (zip 경로 → { entry, name })
 * @param {(p: { done: number, total: number, title: string, ok: boolean }) => void} onProgress
 * @returns {Promise<{ success: number, failed: number, contentFailed: number, errors: Array }>}
 */
export async function runImport(nodes, ctx, onProgress) {
  const { orgId, userId, assets } = ctx;
  const ordered = orderForImport(nodes);
  const total = ordered.length;

  // 노드 id → 생성된 page id 매핑 (parent_id 연결용)
  const createdIds = new Map();
  let success = 0;
  let failed = 0;
  let contentFailed = 0;
  const errors = [];
  let positionBase = Date.now();

  // ── 0) 첨부 업로드 + zip 경로 → public URL 매핑 (참조된 것만, 순차) ──
  const urlByPath = await uploadZipAssets(ordered, assets, errors, onProgress);

  for (let i = 0; i < ordered.length; i += 1) {
    const node = ordered[i];
    const parentId = node.parentNode ? createdIds.get(node.parentNode.id) || null : null;
    positionBase += 1;

    let pageOk = false;
    try {
      // 1) 페이지 생성
      // eslint-disable-next-line no-await-in-loop
      const { data: page, error: pageErr } = await supabase
        .schema('workspace')
        .from('pages')
        .insert({
          org_id: orgId,
          type: 'document',
          title: node.title,
          icon: '📄',
          parent_id: parentId,
          created_by: userId,
          position: positionBase,
        })
        .select('id')
        .single();

      if (pageErr || !page) {
        throw pageErr || new Error('페이지 생성 결과 없음');
      }
      createdIds.set(node.id, page.id);
      pageOk = true;
      success += 1;

      // 2) 본문 변환 + 저장 (실패해도 페이지 자체는 유지)
      // 첨부 상대경로 → public URL/임베드·파일 마커 재작성 (업로드 실패분은 원문 유지)
      const fixedMd = urlByPath.size
        ? rewriteZipAttachments(node.md, node, assets, urlByPath)
        : node.md;
      // eslint-disable-next-line no-await-in-loop
      const ystate = await markdownToYstate(fixedMd);
      if (ystate) {
        // eslint-disable-next-line no-await-in-loop
        const { error: contentErr } = await supabase
          .schema('workspace')
          .from('page_content')
          .upsert(
            { page_id: page.id, ystate, updated_at: new Date().toISOString() },
            { onConflict: 'page_id' }
          );
        if (contentErr) {
          contentFailed += 1;
          errors.push({ title: node.title, stage: 'content', message: contentErr.message });
        }
      } else if (node.md && node.md.trim()) {
        // 본문이 있었는데 변환 실패 → 폴백(빈 본문). 사실 보고용 카운트.
        contentFailed += 1;
        errors.push({ title: node.title, stage: 'content', message: '본문 변환 실패(폴백: 빈 본문)' });
      }
    } catch (e) {
      if (!pageOk) {
        failed += 1;
        errors.push({ title: node.title, stage: 'page', message: e?.message || String(e) });
      }
    }

    if (onProgress) onProgress({ done: i + 1, total, title: node.title, ok: pageOk });
  }

  return { success, failed, contentFailed, errors };
}
